/**
 * React Query hooks for header badge counts (notifications, emails)
 * 
 * These are lightweight queries that poll periodically to keep badges updated.
 * The header should NOT wait for these - show 0 or placeholder while loading.
 */

import { useQuery, UseQueryOptions, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/apiClient';
import { queryOptions } from '../lib/queryClient';

/**
 * Hook to fetch unread notifications count
 * 
 * @param company - Company slug (required)
 * @param userId - User ID (optional, but recommended to prevent early calls)
 * @param options - Additional React Query options
 * @returns Query result with count
 * 
 * @example
 * ```tsx
 * const { client } = useClient();
 * const { data } = useNotificationsCount(company, client?._id);
 * const count = data?.count || 0;
 * ```
 */
export function useNotificationsCount(
  company: string | undefined,
  userId?: string | undefined,
  options?: Omit<UseQueryOptions<{ count: number }>, 'queryKey' | 'queryFn'>
) {
  return useQuery<{ count: number }>({
    queryKey: ['header', 'notificationsCount', company || '', userId || ''],
    queryFn: async () => {
      if (!company) {
        throw new Error('Company is required');
      }

      // CRITICAL: Superadmin users don't have company-based notifications
      // Skip API call for superadmin routes to prevent 404 errors
      if (company === 'superadmin' || company === 'undefined') {
        return { count: 0 };
      }

      // IMPORTANT: Only make API call when user is authenticated
      // This prevents 500 errors during login/bootstrap phases
      if (!userId) {
        return { count: 0 };
      }

      try {
        const { data } = await apiClient.post<{ ok: boolean; data: { count: number } }>(
          `/api/${company}/rpc/notifications/unread-count`,
          {},
          { withCredentials: true }
        );

        if (!data?.ok) {
          throw new Error((data as any)?.error || 'Failed to fetch notifications count');
        }

        return data.data || { count: 0 };
      } catch (err: any) {
        // CRITICAL FIX: Handle 503 (Service Unavailable) - circuit breaker is open
        // Return 0 gracefully instead of throwing to prevent error spam
        if (err?.response?.status === 503 || err?.response?.status === 404 || err?.response?.status === 401) {
          return { count: 0 };
        }
        // Handle connection refused (server not running)
        if (err?.code === 'ERR_CONNECTION_REFUSED' || err?.message?.includes('ERR_CONNECTION_REFUSED')) {
          return { count: 0 };
        }
        throw err;
      }
    },
    // IMPORTANT: Only enable query when company AND user are ready
    // This prevents API calls before authentication is complete
    // Also disable for superadmin routes (no company-based notifications)
    enabled: !!company && !!userId && company !== 'superadmin' && company !== 'undefined',
    staleTime: 10 * 1000, // 10 seconds - data is fresh for 10s
    gcTime: 60 * 1000, // 60 seconds - cache for 60s (formerly cacheTime)
    refetchInterval: 15 * 1000, // Poll every 15 seconds
    refetchOnWindowFocus: false, // CRITICAL: Disable refetch on focus to prevent spam on network errors
    refetchOnMount: true,
    refetchOnReconnect: true, // Only refetch when network reconnects
    // CRITICAL FIX: Don't retry on 503 errors (circuit breaker is open, retrying won't help)
    retry: (failureCount, error: any) => {
      // Don't retry on 503 (Service Unavailable), 401 (Unauthorized), or connection errors
      const isNetworkError = !error?.response;
      const isConnectionRefused = error?.code === 'ECONNREFUSED' || 
                                  error?.code === 'ERR_CONNECTION_REFUSED' ||
                                  error?.message?.includes('ERR_CONNECTION_REFUSED');
      
      if (error?.response?.status === 503 || 
          error?.response?.status === 401 ||
          error?.response?.status === 404 ||
          isConnectionRefused ||
          isNetworkError) {
        return false;
      }
      // Retry up to 2 times for other errors
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
    ...options,
  });
}

/**
 * Hook to fetch unread emails count
 * 
 * @param company - Company slug (required)
 * @param options - Additional React Query options
 * @returns Query result with count
 * 
 * @example
 * ```tsx
 * const { data } = useEmailsCount(company);
 * const count = data?.count || 0;
 * ```
 */
export function useEmailsCount(
  company: string | undefined,
  options?: Omit<UseQueryOptions<{ count: number }>, 'queryKey' | 'queryFn'>
) {
  return useQuery<{ count: number }>({
    queryKey: ['header', 'emailsCount', company || ''],
    queryFn: async () => {
      if (!company) {
        throw new Error('Company is required');
      }

      try {
        const { data } = await apiClient.post<{ ok: boolean; data: { unreadCount: number } }>(
          `/api/${company}/rpc/email/unread-count`,
          {},
          { withCredentials: true }
        );

        if (!data?.ok) {
          throw new Error((data as any)?.error || 'Failed to fetch emails count');
        }

        // Handle both { unreadCount } and { count } formats
        const count = data.data?.unreadCount ?? (data.data as any)?.count ?? 0;
        return { count };
      } catch (err: any) {
        // CRITICAL FIX: Handle 503 (Service Unavailable) - circuit breaker is open
        // Silently handle errors - return 0 count to prevent UI breaking
        // This is especially important for clients who don't have email access (403)
        if (err?.response?.status === 503 || 
            err?.response?.status === 403 || 
            err?.response?.status === 404 ||
            err?.code === 'ERR_CONNECTION_REFUSED' ||
            err?.message?.includes('ERR_CONNECTION_REFUSED')) {
          return { count: 0 };
        }
        // For other errors, return 0 gracefully
        return { count: 0 };
      }
    },
    enabled: !!company,
    staleTime: 10 * 1000, // 10 seconds - data is fresh for 10s
    gcTime: 60 * 1000, // 60 seconds - cache for 60s (formerly cacheTime)
    refetchInterval: 60 * 1000, // Poll every 60 seconds (emails change less frequently)
    refetchOnWindowFocus: false, // CRITICAL: Disable refetch on focus to prevent spam on network errors
    refetchOnMount: true,
    refetchOnReconnect: true, // Only refetch when network reconnects
    // CRITICAL FIX: Don't retry on 503 errors (circuit breaker is open, retrying won't help)
    retry: (failureCount, error: any) => {
      // Don't retry on 503 (Service Unavailable), 403 (Forbidden), 404 (Not Found), or connection errors
      const isNetworkError = !error?.response;
      const isConnectionRefused = error?.code === 'ECONNREFUSED' || 
                                  error?.code === 'ERR_CONNECTION_REFUSED' ||
                                  error?.message?.includes('ERR_CONNECTION_REFUSED');
      
      if (error?.response?.status === 503 || 
          error?.response?.status === 403 ||
          error?.response?.status === 404 ||
          isConnectionRefused ||
          isNetworkError) {
        return false;
      }
      // Retry up to 2 times for other errors
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
    ...options,
  });
}

/**
 * Helper to optimistically set notifications count to 0
 * Call this when user opens notifications panel
 */
export function useOptimisticNotificationsCount() {
  const queryClient = useQueryClient();

  return (company: string) => {
    queryClient.setQueryData(['header', 'notificationsCount', company], { count: 0 });
  };
}

/**
 * Helper to optimistically set emails count to 0
 * Call this when user opens emails panel
 */
export function useOptimisticEmailsCount() {
  const queryClient = useQueryClient();

  return (company: string) => {
    queryClient.setQueryData(['header', 'emailsCount', company], { count: 0 });
  };
}

